import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject, forkJoin, switchMap } from 'rxjs';
import { DateTimeUtils } from 'src/app/shared/utils/date-time.utils';
import { Emergency, EmergencyClient, EmergencyMaterial, EmergencyMaterialClient, EmergencyMechanisation, EmergencyMechanisationClient, EmergencyStatus, EmergencyStatusClient, Material, MaterialClient, MechanisationClient, Mechanisations, RegisteredSignal, TipAvaria, TipAvariaClient, Unit, VIKUnitClient, VidAvaria, VidAvariaClient, WorkingGroupClient, WorkingGroups } from 'src/app/web-api-client';

@Component({
  selector: 'app-add-emergency',
  templateUrl: './add-emergency.component.html',
  styleUrls: ['./add-emergency.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddEmergencyComponent implements OnInit {
  @Input() closedSignalsList: RegisteredSignal[] = [];
  @Input() emergency: Emergency;

  public onClose = new Subject<void>();
  public onEmergencyCreated = new Subject<Emergency>();

  public form: FormGroup;
  public loading = true;

  public selectedRegisteredSignal: RegisteredSignal;
  public avariaTypesList: TipAvaria[] = [];
  public vidAvariaList: VidAvaria[] = [];
  public unitsList: Unit[] = [];

  public availableMaterialsList: Material[] = [];
  public emergencyMaterialsList: EmergencyMaterial[] = [];
  public usedEmegencyMaterialsList: EmergencyMaterial[] = [];

  public availableMechanisationsList: Mechanisations[] = [];
  public emergencyMechanisationsList: EmergencyMechanisation[] = [];
  public usedEmergencyMechanisationsList: EmergencyMechanisation[] = [];
  public wGroupsList: WorkingGroups[] = [];

  public emergencyStatuses: EmergencyStatus[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly vidAvariaClient: VidAvariaClient,
    private readonly tipAvariaService: TipAvariaClient,
    private readonly vikUnitClient: VIKUnitClient,
    private readonly materialClient: MaterialClient,
    private readonly emergencyMaterialClient: EmergencyMaterialClient,
    private readonly emergencyMechanisationClient: EmergencyMechanisationClient,
    private readonly emergencyClientService: EmergencyClient,
    private readonly workingGroupClient: WorkingGroupClient,
    private readonly mechanisationClient: MechanisationClient,
    private readonly emergencyStatusClient: EmergencyStatusClient,
    private toastr: ToastrService,
    public readonly activeModal: NgbActiveModal,
  ) { }

  ngOnInit(): void {
    if (this.emergency) {
      this.selectedRegisteredSignal = this.closedSignalsList.find(s => s.id === this.emergency.registeredSignalId);

    

    }



    this.loadData();
    this.initializeForm();
    this.changeDetectorRef.detectChanges();
  }

  get incidentLocation(): google.maps.LatLngLiteral | null {
    if (!this.selectedRegisteredSignal) {
      return null;
    }

    return {
      lat: +this.selectedRegisteredSignal.latitude,
      lng: +this.selectedRegisteredSignal.longitude
    }
  }

  get emergencyLocation(): google.maps.LatLngLiteral {
    return this.form.get('position').value;
  }

  public toLocalDateTime(date: Date) {
    return DateTimeUtils.toFormLocalDateTime(date);
  }

  public getUnitTitle(unitId: number): string {
    return this.unitsList.find(u => u.id === unitId)?.title;
  }

  public getSignalWorkingGroupTitle(wGroupId: number): string {
    return this.wGroupsList.find(wg => wg.id === wGroupId)?.title || '';
  }

  public onRegisteredSignalSelected($event: number) {
    this.selectedRegisteredSignal = this.closedSignalsList.find(s => s.id === $event);

    const position: google.maps.LatLngLiteral = this.selectedRegisteredSignal != null
      ? { lat: +this.selectedRegisteredSignal.latitude, lng: +this.selectedRegisteredSignal.longitude }
      : null;

    this.form.patchValue({
      emergencyTitle: this.selectedRegisteredSignal?.title,
      emergencyDescription: this.selectedRegisteredSignal?.description,
      idTipAvaria: this.selectedRegisteredSignal?.idTipAvaria,
      workingGroupsId: this.selectedRegisteredSignal?.workingGroupsId,
      position: position
    });

    this.changeDetectorRef.detectChanges();
  }

  public onEmergencyMaterialsUpdated(emergencyMaterials: EmergencyMaterial[]) {
    this.usedEmegencyMaterialsList = emergencyMaterials.slice();
  }

  public onEmergencyMechanisationsUpdated(emergencyMechanisations: EmergencyMechanisation[]) {
    this.usedEmergencyMechanisationsList = emergencyMechanisations.slice();
  }

  public onLocationUpdated($event: google.maps.LatLngLiteral) {
    this.form.patchValue({
      position: $event
    });
  }

  public onSubmit($event) {
    if(this.emergency) {
      this.updateEmergency();
    } else {
      this.saveEmergency();
    }
  }

  public closeModal() {
    this.activeModal.close();
    this.onClose.next();
  }

  private initializeForm() {
    this.loading = true;

    const registeredSignalId = this.emergency?.registeredSignalId || this.selectedRegisteredSignal?.id;
    const workingGroupsId = this.emergency?.workingGroupsId || this.selectedRegisteredSignal?.workingGroupsId;

    this.form = this.fb.group({
      registeredSignalId: [{ value: registeredSignalId, disabled: !!this.emergency }, Validators.required],
      idTipAvaria: [{ value: this.selectedRegisteredSignal?.idTipAvaria, disabled: false }, Validators.required],
      workingGroupsId: [{ value: workingGroupsId, disabled: false }, Validators.required],
      emergencyTitle: [{ value: this.selectedRegisteredSignal?.title, disabled: false }, Validators.required],
      emergencyDescription: new FormControl({ value: this.selectedRegisteredSignal?.description, disabled: false }),
      position: [null, Validators.required]
    });
  }

  private loadData() {
    forkJoin([
      this.tipAvariaService.getList(),
      this.vidAvariaClient.getList(),
      this.vikUnitClient.getList(),
      this.materialClient.getList(),
      this.emergencyMaterialClient.getList(),
      this.emergencyMechanisationClient.getList(),
      this.workingGroupClient.getList(),
      this.mechanisationClient.getList(),
      this.emergencyStatusClient.getList()
    ]).subscribe(([avariaTypesList, vidAvariaList, unitsList, materialsList, emMaterialsList, emMechanisationsList, wGroupsList, mechanisationsList, emergencyStatuses]) => {
      this.avariaTypesList = avariaTypesList;
      this.vidAvariaList = vidAvariaList;
      this.unitsList = unitsList;
      this.availableMaterialsList = materialsList;
      this.emergencyMaterialsList = emMaterialsList;
      this.wGroupsList = wGroupsList;
      this.availableMechanisationsList = mechanisationsList;
      this.emergencyMechanisationsList = emMechanisationsList;

      this.emergencyStatuses = emergencyStatuses;


      this.updateEmergencyMaterialsList();
      this.updateEmergencyMechanisationsList();

      this.loading = false;
      this.changeDetectorRef.detectChanges();

      //console.log("Data loaded");
      //console.log("VidAvariaList:");
      //console.log(vidAvariaList);
      // console.log("UnitsList:");
      // console.log(unitsList);
      // console.log("MaterialsList:");
      // console.log(materialsList);
      // console.log("EmergencyMaterialsList:");
      // console.log(emergencyMaterialsList);
      // console.log("MechanisationsList:");
      // console.log(mechanisationsList);
    });
  }

  public getEmergencyStatus(): string {

    if (this.emergency) {
      return this.emergencyStatuses.find(d => d.id == this.emergency.emergencyStatusId).title;
    }

    return this.emergencyStatuses.find(d => d.id == 1).title;
  }

  private updateEmergencyMaterialsList() {
    if (this.emergency) {
      this.usedEmegencyMaterialsList = this.emergencyMaterialsList
        .filter(em => em.emergencyId === this.emergency.id)
        .map(em => {
          em.material = this.availableMaterialsList.find(m => m.id === em.materialId);
          return em;
        });

      this.availableMaterialsList = this.availableMaterialsList.filter(material => {
        return !this.usedEmegencyMaterialsList.some(em => em.materialId === material.id);
      });
    }
  }

  private updateEmergencyMechanisationsList() {
    if (this.emergency) {
      this.usedEmergencyMechanisationsList = this.emergencyMechanisationsList
        .filter(em => em.emergencyId === this.emergency.id);

      this.availableMechanisationsList = this.availableMechanisationsList.filter(mechanisation => {
        return !this.usedEmergencyMechanisationsList.some(em => em.mechanisationId === mechanisation.id);
      });
    }
  }

  private buildEmergencyFromFormData() {
    const formValues = this.form.getRawValue();
    console.log(formValues);

    let emergency = new Emergency();
    emergency.registeredSignalId = formValues.registeredSignalId;
    emergency.title = formValues.emergencyTitle;
    emergency.description = formValues.emergencyDescription;
    emergency.latitude = formValues.position.lat.toString();
    emergency.longitude = formValues.position.lng.toString();
    emergency.idTipAvaria = formValues.idTipAvaria;
    emergency.workingGroupsId = formValues.workingGroupsId;

    return emergency;
  }



  private saveEmergency() {
    if (!this.form.valid) {
      return;
    }

    const emergency = this.buildEmergencyFromFormData();
    this.emergencyClientService.create(emergency).pipe(
      switchMap(createdEmergencyId => {
        return forkJoin([
          ...this.usedEmegencyMaterialsList.map(emergencyMaterial => {
            emergencyMaterial.emergencyId = createdEmergencyId;

            const tempEmergencyMaterial = JSON.parse(JSON.stringify(emergencyMaterial));
            delete tempEmergencyMaterial.material;

            return this.emergencyMaterialClient.create(tempEmergencyMaterial);
          }),
          ...this.usedEmergencyMechanisationsList.map(emMechanisation => {
            emMechanisation.emergencyId = createdEmergencyId;

            const tempEmMechanisation = JSON.parse(JSON.stringify(emMechanisation));
            delete tempEmMechanisation.mechanisation;

            return this.emergencyMechanisationClient.create(tempEmMechanisation);
          })
        ]);
      })
    ).subscribe(_result => {

      this.toastr.success("Успешно Създадена", "Авария", {
        timeOut: 3000,
        closeButton: true,
        positionClass: "toast-top-right",
      });

      this.onEmergencyCreated.next(emergency);
      this.closeModal();
    });
  }

  private updateEmergency() {
    if (!this.form.valid) {
      return;
    }

    const emergency = this.buildEmergencyFromFormData();
    emergency.id = this.emergency.id;

    this.emergencyClientService.update(emergency).pipe(
      switchMap(result => {
        return forkJoin([
          ...this.usedEmegencyMaterialsList.map(emergencyMaterial => {
            emergencyMaterial.emergencyId = emergency.id;

            const tempEmergencyMaterial = JSON.parse(JSON.stringify(emergencyMaterial));
            delete tempEmergencyMaterial.material;

            return this.emergencyMaterialClient.create(tempEmergencyMaterial);
          }),
          ...this.usedEmergencyMechanisationsList.map(emMechanisation => {
            emMechanisation.emergencyId = emergency.id;

            const tempEmMechanisation = JSON.parse(JSON.stringify(emMechanisation));
            delete tempEmMechanisation.mechanisation;

            return this.emergencyMechanisationClient.create(tempEmMechanisation);
          })
        ]);
      })
    ).subscribe(_result => {
      this.onEmergencyCreated.next(emergency);
      this.closeModal();
    });
  }
}
